import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Slide,
  Typography,
  Link,
  Grid,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { EasyCheckButton } from '../../common/Controls';
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Business,
  Email,
  Phone,
  Lock,
  PersonAddRounded,
  NavigateNext
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { themeUtils } from '../../../theme';
import PaymentStep from '../Payment/PaymentStep';
import SignupHelpLinks from './SignupHelpLinks';
import { loadGA, trackEvent } from '../../../utils/analytics';
import { stripeService } from '../../../services/payment/stripeService';

const SignupContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: themeUtils.gradients.backgroundLight,
  overflowY: 'auto',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: themeUtils.gradients.primary,
    opacity: 0.05,
    zIndex: 0,
    pointerEvents: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

const SignupCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '24px',
  padding: theme.spacing(5),
  boxShadow: 'none',
  border: `1px solid ${themeUtils.colors.blue['blue-100']}`,
  maxWidth: '484px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  margin: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    margin: theme.spacing(1),
    borderRadius: 12,
    padding: theme.spacing(1.5),
    overflowX: 'hidden',
    overflowY: 'auto',
  },
}));

const LogoSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 700,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textDecoration: 'none',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.6rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
  },
}));

const LogoEasy = styled('span')(({ theme }) => ({
  color: '#0f172a', // Dark gray/black
}));

const LogoCheck = styled('span')(({ theme }) => ({
  color: '#2563eb', // Blue
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '1.4rem',
  marginBottom: theme.spacing(1),
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.95rem',
  marginBottom: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
    '& fieldset': {
      borderColor: themeUtils.colors.blue['blue-100'],
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: themeUtils.colors.blue['blue-300'],
    },
    '&.Mui-focused fieldset': {
      borderColor: themeUtils.colors.blue['blue-600'],
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      color: themeUtils.colors.blue['blue-600'],
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '0 8px',
      marginLeft: '-4px',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
  },
}));

const SignInSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  borderTop: `1px solid ${themeUtils.colors.blue['blue-100']}`,
}));



const AnimatedBox = styled(Box)(({ theme }) => ({
  animation: 'fadeInUp 0.6s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const SignupForm = () => {
  console.log('🔵 SIGNUPFORM COMPONENT LOADED!');
  console.log('🔵 Current URL on component load:', window.location.href);
  
  const theme = useTheme();
  const navigate = useNavigate();
  const { signup } = useEasyCheck();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [signupData, setSignupData] = useState(null);
  
  console.log('🔵 SignupForm initial state - currentStep:', currentStep, 'loading:', loading);

  // Load GA4 on the signup acquisition flow only (covers both the contact form and the
  // payment stage, which render under this same /signup route). No-op in dev / when authed.
  useEffect(() => { loadGA(); }, []);

  const steps = ['Create Account', 'Payment', 'Complete'];

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      username: '',
      password: '',
      phoneNumber: ''
    }
  });

  const onSubmit = async (data) => {
    console.log('🟦 SIGNUP FORM SUBMITTED!');
    console.log('🟦 Current step before submit:', currentStep);
    console.log('🟦 Current URL before submit:', window.location.href);
    console.log('🟦 Form data received:', { ...data, password: '[HIDDEN]' });
    
    try {
      setLoading(true);
      setError('');
      setValidationErrors([]);
      
      console.log('🔄 Processing signup form data:', data);
      
      // Store signup data and proceed to payment step
      setSignupData(data);
      
      // Store in session storage for payment success page
      sessionStorage.setItem('signupData', JSON.stringify(data));
      
      console.log('✅ Signup data stored, moving to payment step');

      // Fire-and-forget lead notification email to admin
      stripeService.notifyLead({
        firstName: data.firstName,
        lastName: data.lastName,
        companyName: data.companyName,
        email: data.username,
        phoneNumber: data.phoneNumber || '',
        stage: 'contact_form',
      });

      // GA4 funnel: lead captured at the same moment the lead email fires.
      trackEvent('generate_lead', { company: data.companyName, currency: 'AED' });

      // Move to payment step
      setCurrentStep(1);
      
      console.log('🟦 setCurrentStep(1) called successfully');
      console.log('🟦 Current URL after step change:', window.location.href);
      
    } catch (err) {
      console.error('❌ Signup form error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
      console.log('🟦 onSubmit function completed');
    }
  };

  const handleBackToAccountInfo = () => {
    setCurrentStep(0);
    setError('');
  };

  const handlePaymentSuccess = () => {
    setCurrentStep(2);
    setSignupSuccess(true);
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const emailPattern = {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: 'Please enter a valid email address'
  };

  const phonePattern = {
    value: /^[\+\d]{0,15}$/,
    message: 'Only digits and + allowed, max 15 characters'
  };

  // Payment step
  console.log('🟡 SignupForm render - currentStep:', currentStep);
  console.log('🟡 SignupForm render - signupData:', signupData ? 'EXISTS' : 'NULL');
  
  if (currentStep === 1) {
    console.log('🟡 Rendering PaymentStep component...');
    return (
      <SignupContainer>
        <AnimatedBox>
          <LogoSection>
            <LogoContainer>
              <LogoText>
                <LogoEasy>Easy</LogoEasy>
                <LogoCheck>Check</LogoCheck>
              </LogoText>
            </LogoContainer>
          </LogoSection>

          <Stepper activeStep={currentStep} alternativeLabel sx={{ 
            mb: 2,
            '& .MuiStepLabel-label': {
              fontSize: '0.8rem',
            }
          }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <PaymentStep
            signupData={signupData}
            onBack={handleBackToAccountInfo}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />

        </AnimatedBox>
      
      {/* Copyright text */}
      <Box sx={{
        width: '100%',
        textAlign: 'center',
        mt: 3,
        zIndex: 1,
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          © 2025 EasyCheck. All rights reserved.
        </Typography>
      </Box>
      </SignupContainer>
    );
  }

  return (
    <SignupContainer>
      <AnimatedBox>
        <LogoSection>
          <LogoContainer>
            <LogoText>
              <LogoEasy>Easy</LogoEasy>
              <LogoCheck>Check</LogoCheck>
            </LogoText>
          </LogoContainer>
          <WelcomeText sx={{ color: '#64748b' }}>Sign up</WelcomeText>
        </LogoSection>

        <Stepper activeStep={currentStep} alternativeLabel sx={{ 
          mb: 2,
          '& .MuiStepLabel-label': {
            fontSize: '0.8rem',
          }
        }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <SignupCard>
          <Box component="form" onSubmit={(e) => {
            console.log('🔶 FORM SUBMIT EVENT TRIGGERED!');
            console.log('🔶 Event:', e);
            console.log('🔶 About to call handleSubmit...');
            return handleSubmit(onSubmit)(e);
          }}>
            <Stack spacing={3}>
              {error && (
                <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: '12px',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.2)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}

        {validationErrors.length > 0 && (
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.2)'
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Please fix the following errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              {validationErrors.map((error, index) => (
                <li key={index}>
                  <Typography variant="body2">
                    {error.field}: {error.message}
                  </Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Controller
            name="firstName"
            control={control}
            rules={{ required: 'First name is required' }}
            render={({ field }) => (
              <StyledTextField
                {...field}
                label="First Name"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            rules={{ required: 'Last name is required' }}
            render={({ field }) => (
              <StyledTextField
                {...field}
                label="Last Name"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Stack>

        <Controller
          name="companyName"
          control={control}
          rules={{ required: 'Company name is required' }}
          render={({ field }) => (
                          <StyledTextField
                {...field}
                label="Company Name"
                fullWidth
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
            />
          )}
        />

        <Controller
          name="username"
          control={control}
          rules={{ 
            required: 'Email is required',
            pattern: emailPattern
          }}
          render={({ field }) => (
                          <StyledTextField
                {...field}
                label="Email Address"
                type="email"
                fullWidth
                error={!!errors.username}
                helperText={errors.username?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
            />
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: 'Phone number is required',
            pattern: phonePattern
          }}
          render={({ field }) => (
                          <StyledTextField
                {...field}
                onChange={(e) => {
                  const stripped = e.target.value.replace(/[^+\d]/g, '').slice(0, 15);
                  field.onChange(stripped);
                }}
                label="Phone Number"
                fullWidth
                inputProps={{ maxLength: 15 }}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                }}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{ 
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters'
            }
          }}
          render={({ field }) => (
            <StyledTextField
              {...field}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              error={!!errors.password}
              helperText={errors.password?.message}
                              InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ 
                          color: themeUtils.colors.blue['blue-600'],
                          opacity: 0.7,
                          '&:hover': {
                            backgroundColor: `${themeUtils.colors.blue['blue-50']}`,
                            opacity: 1,
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
            />
          )}
        />

              <EasyCheckButton
                buttonType="auth"
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                size="large"
                startIcon={<NavigateNext />}
                onClick={(e) => {
                  console.log('🔷 CONTINUE TO PAYMENT BUTTON CLICKED!');
                  console.log('🔷 Event:', e);
                  console.log('🔷 Current step:', currentStep);
                  console.log('🔷 Form loading state:', loading);
                }}
                sx={{
                  background: themeUtils.gradients.primary,
                  '&:hover': {
                    background: themeUtils.gradients.primaryDark,
                  },
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  boxShadow: 'none',
                }}
              >
                {loading ? 'Processing...' : 'Continue to Payment'}
              </EasyCheckButton>
            </Stack>
          </Box>

          <SignupHelpLinks stage="signup" />

          <SignInSection>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => navigate('/login')}
                sx={{ 
                  color: themeUtils.colors.blue['blue-600'],
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: themeUtils.colors.blue['blue-700'],
                  }
                }}
              >
                Sign In
              </Link>
            </Typography>
          </SignInSection>
        </SignupCard>
      </AnimatedBox>
      
      {/* Copyright text */}
      <Box sx={{
        width: '100%',
        textAlign: 'center',
        mt: 3,
        zIndex: 1,
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          © 2025 EasyCheck. All rights reserved.
        </Typography>
      </Box>
    </SignupContainer>
  );
};

export default SignupForm; 